/* Arrows, dot pagination and a slow self-advance for the horizontal product
   rail, the way the reference drives its carousel. The track is already a
   scroll-snap scroller, so this only steps it and mirrors its position: no
   second source of truth for which card is showing. */
(function () {
  /* Slow enough to read a card and a price before it moves, which is the
     point of the thing. */
  const ADVANCE_MS = 5000;
  /* After a swipe or an arrow, the rail belongs to the visitor for a while.
     Taking it straight back would fight them. */
  const RESUME_MS = 9000;

  class BrandRail extends HTMLElement {
    connectedCallback() {
      this.setup();

      /* Two of these rows are filled after the fact: the recommendations are
         fetched and written in by Dawn, and recently viewed is built from the
         visitor's own history. In both cases this element connects before the
         cards exist, the lookups below come back empty, and without this it
         would sit there for the rest of the page's life having decided there
         was nothing to drive. Watching for the children to arrive is what
         makes the row work whether its markup came with the page or after
         it. */
      if (!this.track) {
        this.waiting = new MutationObserver(() => {
          if (!this.querySelector('[data-rail-track]')) return;
          this.waiting.disconnect();
          this.waiting = null;
          this.setup();
        });
        this.waiting.observe(this, { childList: true, subtree: true });
      }
    }

    setup() {
      this.track = this.querySelector('[data-rail-track]');
      this.prev = this.querySelector('[data-rail-prev]');
      this.next = this.querySelector('[data-rail-next]');
      this.dots = this.querySelector('[data-rail-dots]');
      this.bar = this.querySelector('[data-rail-bar]');
      if (!this.track) return;

      this.prev?.addEventListener('click', () => { this.hold(); this.step(-1); });
      this.next?.addEventListener('click', () => { this.hold(); this.step(1); });

      this.onScroll = () => this.sync();
      this.track.addEventListener('scroll', this.onScroll, { passive: true });

      /* Card widths come from a percentage, so a resize changes how many
         pages there are. */
      this.ro = new ResizeObserver(() => this.build());
      this.ro.observe(this.track);

      /* The track can be present and empty, which is what recently viewed
         looks like until its script has read the visitor's history. Counting
         pages then gives one, so the count is taken again once cards land. */
      this.content = new MutationObserver(() => this.build());
      this.content.observe(this.track, { childList: true });

      this.build();
      this.startAuto();
    }

    disconnectedCallback() {
      this.track?.removeEventListener('scroll', this.onScroll);
      this.ro?.disconnect();
      this.io?.disconnect();
      this.waiting?.disconnect();
      this.content?.disconnect();
      clearInterval(this.timer);
      clearTimeout(this.resumeTimer);
      cancelAnimationFrame(this.raf);
      clearTimeout(this.settleTimer);
    }

    get pages() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      if (max <= 1) return 1;
      return Math.ceil(max / this.pageStep_()) + 1;
    }

    /* A page is a viewportful, rounded down to whole cards so the scroll
       still lands on a snap point. Paging by single card gave one dot per
       product, which on a phone was twenty three dots and read as a smear.
       The reference pages by viewport, which is why its row is short enough
       to tell you where you are. */
    pageStep_() {
      const step = this.step_();
      if (!step) return this.track.clientWidth || 1;
      const styles = getComputedStyle(this.track);
      const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
      const perView = Math.max(1, Math.floor((this.track.clientWidth + gap) / step));
      return perView * step;
    }

    step_() {
      /* The rail drives more than one kind of row now, so the card is taken
         from the track rather than from a class only one of them carries. */
      const item = this.track.querySelector('.brand-rail__item') || this.track.firstElementChild;
      if (!item) return this.track.clientWidth;
      const styles = getComputedStyle(this.track);
      const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
      return item.getBoundingClientRect().width + gap;
    }

    build() {
      if (!this.dots) return this.sync();
      const count = this.pages;
      /* One page means the rail already shows everything, so the controls
         would be decoration. */
      this.toggleAttribute('data-single', count <= 1);
      /* Past fourteen the dots are shrunk rather than dropped, because with
         one card to a phone screen the count is the catalogue's and dropping
         them would leave the rail with no position at all. */
      this.toggleAttribute('data-many', count > 14);
      if (this.dots.childElementCount !== count) {
        this.dots.innerHTML = '';
        for (let i = 0; i < count; i += 1) {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'brand-rail__dot';
          b.setAttribute('aria-label', `Go to slide ${i + 1}`);
          b.addEventListener('click', () => {
            this.hold();
            this.scrollToX(i * this.pageStep_());
          });
          this.dots.appendChild(b);
        }
      }
      this.sync();
    }

    step(dir) {
      this.scrollToX(this.track.scrollLeft + dir * this.pageStep_());
    }

    /* Every programmatic move goes through here, animated by hand.

       Two browser features had to go. Safari on iOS will not animate a scroll
       inside a container whose scroll-snap-type is mandatory: the snap wins
       and the scroll is thrown away. Turning the snap off for the move fixes
       that, but only if it goes back on once the move has finished, and the
       event that says so, scrollend, is missing before Safari 17.4. A timer
       in its place either restores the snap mid-flight, and Safari cricks the
       track straight back to where it started, or sits there long after the
       move is done. Either way the rail looked frozen on a phone and ran
       fine on a desktop, which is exactly what was reported.

       Stepping scrollLeft frame by frame removes both. Nothing here waits on
       smooth scrolling and nothing waits on scrollend: the animation knows
       when it is over, because it is the one running it. */
    scrollToX(left) {
      const track = this.track;
      const max = track.scrollWidth - track.clientWidth;
      const to = Math.max(0, Math.min(left, max));
      const from = track.scrollLeft;
      const distance = to - from;
      if (Math.abs(distance) < 1) return;

      cancelAnimationFrame(this.raf);
      clearTimeout(this.settleTimer);
      track.style.scrollSnapType = 'none';
      this.animating = true;

      const DURATION = 420;
      const start = performance.now();
      /* Ease in and out, so it reads as a carousel rather than a jump. */
      const ease = (t) => (t < 0.5 ? 2 * t * t : 1 - ((-2 * t + 2) ** 2) / 2);

      const frame = (now) => {
        const p = Math.min(1, (now - start) / DURATION);
        track.scrollLeft = from + distance * ease(p);
        if (p < 1) {
          this.raf = requestAnimationFrame(frame);
          return;
        }
        track.style.scrollSnapType = '';
        /* Putting the snap back makes the browser settle the track, which
           fires one more scroll event. Held open a moment so that one is not
           mistaken for a swipe and does not pause the rail. */
        this.settleTimer = setTimeout(() => { this.animating = false; }, 250);
      };
      this.raf = requestAnimationFrame(frame);
    }

    sync() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const at = this.track.scrollLeft;
      if (this.prev) this.prev.disabled = at <= 2;
      if (this.next) this.next.disabled = at >= max - 2;

      /* A bar rather than dots, where the row is short enough that dots would
         be two marks and a bar says how far along you are without pretending
         to be a control. Width is the share of the row on screen, position is
         how far it has been pushed. */
      if (this.bar) {
        const share = this.track.clientWidth / this.track.scrollWidth;
        const progress = max > 0 ? at / max : 0;
        this.bar.style.width = (share * 100).toFixed(2) + '%';
        this.bar.style.left = ((1 - share) * progress * 100).toFixed(2) + '%';
      }

      if (!this.dots) return;
      const active = Math.round(at / this.pageStep_());
      [...this.dots.children].forEach((d, i) =>
        d.setAttribute('aria-current', i === active ? 'true' : 'false')
      );
    }

    /* Self-advance.

       Four things stop it, and all four matter. A visitor reaching for a card
       should not have it slide away, so touching, hovering or tabbing into the
       rail holds it, and a swipe or an arrow holds it for longer still. A rail
       off the bottom of the page moves nothing anyone can see and only costs
       battery, so it waits until it is on screen. A backgrounded tab is the
       same argument. And someone who has asked their system for less movement
       has asked for this too, so it never starts.

       Only real input holds it. Listening to the scroll event instead would
       have been simpler and wrong: this scrolls the track itself, so every
       advance would have paused the next one and the rail would move once and
       stop. */
    startAuto() {
      const still = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (still.matches) return;

      this.hover = false;
      this.focused = false;
      this.held = false;
      this.onScreen = false;

      /* A swipe holds the rail, a page scroll must not. On a phone the page
         is dragged with a finger that lands wherever it lands, very often on
         the rail, so holding on touchstart froze the rail for nine seconds
         every time someone scrolled down to it: it was still by the time it
         came into view and looked broken.

         So the hold comes off the track's own scroll instead, and the
         programmatic scrolls are told apart from a swipe by the snap being
         off, which is true for exactly the length of one. */
      this.track.addEventListener('scroll', () => {
        if (!this.animating) this.hold();
      }, { passive: true });
      this.addEventListener('keydown', () => this.hold());
      this.addEventListener('mouseenter', () => { this.hover = true; });
      this.addEventListener('mouseleave', () => { this.hover = false; });
      this.addEventListener('focusin', () => { this.focused = true; });
      this.addEventListener('focusout', () => { this.focused = false; });

      this.io = new IntersectionObserver(
        ([entry]) => { this.onScreen = entry.isIntersecting; },
        { threshold: 0.35 }
      );
      this.io.observe(this);

      this.timer = setInterval(() => this.tick(), ADVANCE_MS);
    }

    hold() {
      this.held = true;
      clearTimeout(this.resumeTimer);
      this.resumeTimer = setTimeout(() => { this.held = false; }, RESUME_MS);
    }

    tick() {
      if (this.hover || this.focused || this.held) return;
      if (!this.onScreen || document.hidden) return;
      if (this.pages <= 1) return;

      const max = this.track.scrollWidth - this.track.clientWidth;
      if (this.track.scrollLeft >= max - 2) {
        this.scrollToX(0);
      } else {
        this.step(1);
      }
    }
  }

  if (!customElements.get('brand-rail')) customElements.define('brand-rail', BrandRail);
})();
